
import { defineComponent, ref, computed, onMounted } from "vue";
import { Actions } from "@/store/enums/StoreEnums";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { ElMessage } from "element-plus";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { BALANCE_LABEL } from "@/core/config/labels";
import ApiService from "@/core/services/ApiService";
import type { ElForm } from "element-plus";

type FormInstance = InstanceType<typeof ElForm>;

const balanceLabel = BALANCE_LABEL

export default defineComponent({
  setup() {
    onMounted(() => {
      setCurrentPageBreadcrumbs(`${balanceLabel}新增`, [`${balanceLabel}管理;add_balance_records`])
    })

    const validateMobile = (rule, value, callback) => {
      if (!value) {
        callback(new Error('手机号码不可为空'))
      } else if (!/^\d+$/.test(value)) {
        callback(new Error('手机号码必须为数字'))
      } else {
        callback()
      }
    }

    const validateBalance = (rule, value, callback) => {
      const num = Number(value)
      if (value === '' || value === null || value === undefined) {
        callback(new Error('充值金额不可为空'))
      } else if (isNaN(num) || num <= 0) {
        callback(new Error('充值金额必须大于 0'))
      } else {
        callback()
      }
    }

    const rules = ref({
      mobile: [
        { validator: validateMobile, trigger: 'blur' },
      ],
      balance: [
        { validator: validateBalance, trigger: 'blur' },
      ],
    });

    const formRef = ref<FormInstance>()
    const store = useStore()
    const router = useRouter()
    const add_balance_record = ref({
      mobile: '',
      balance: 0,
      note: ''
    })

    const confirmDialogVisible = ref(false)
    const lookupUser = ref({
      id: 0,
      nickname: '',
      mobile: '',
      balance: 0,
      point: 0,
      exp: 0,
    })
    const lookupLoading = ref(false)

    const projectedBalance = computed(() => {
      const current = Number(lookupUser.value.balance) || 0
      const amount = Number(add_balance_record.value.balance) || 0
      return (current + amount).toFixed(2)
    })

    const onCancel = () => {
      router.push({ name: "add_balance_records" })
    }

    const isSubmit = ref(false)

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (isSubmit.value || lookupLoading.value) {
        return
      }

      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          // 先查询用户信息，再弹出确认弹窗
          lookupLoading.value = true
          ApiService.setHeader()
          ApiService.get("admin/users", `lookup?mobile=${add_balance_record.value.mobile}`)
            .then(({ data }) => {
              lookupLoading.value = false
              if (data.success) {
                lookupUser.value = data.user
                confirmDialogVisible.value = true
              } else {
                ElMessage.error(data.error_msg || '查无该使用者')
              }
            })
            .catch(() => {
              lookupLoading.value = false
              ElMessage.error('查询用户信息失败，请重试')
            })
        }
      })
    }

    const doCreate = () => {
      if (isSubmit.value) return

      isSubmit.value = true

      let params = {
        add_balance_record: {
          ...add_balance_record.value
        }
      }

      store
        .dispatch(
          Actions.CREATE_ADD_BALANCE_RECORD,
          params
        )
        .then(() => {
          confirmDialogVisible.value = false
          router.push({ name: "add_balance_records" })
        })
        .catch(() => {
          Swal.fire({
            text: store.getters.getErrors,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "确认",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          })
        })
        .finally(() => {
          isSubmit.value = false
        })
    }

    return {
      balanceLabel,
      onSubmit,
      doCreate,
      add_balance_record,
      rules,
      onCancel,
      formRef,
      isSubmit,
      confirmDialogVisible,
      lookupUser,
      lookupLoading,
      projectedBalance,
    }
  },
})
